import { createContext, useEffect, useState } from "react";
import { useStytchOrganization } from "@stytch/react/b2b";
import { trpc } from "../trpc";

type MerchantContextType = {
  merchantId: string;
  merchantType: "rentals" | "development" | null;
  displayName: string;
  enableAnalyticsByMoveInDate: boolean;
  selfConfigEnabled: boolean;
  country: string;
  helpCenterUrl?: string;
  isLoading: boolean;
  leadCategoryDefinitions: {
    label: string;
    value: {
      min?: number;
      max?: number;
    };
  }[];
  broadcastEnabled: boolean;
  collectionsEnabled: boolean;
  calComIntegrationEnabled: boolean;
  leadReassignmentEnabled?: boolean;
  leadReassignmentAllowedEmails?: string[];
};

const MerchantContext = createContext<MerchantContextType>({
  merchantId: "",
  merchantType: null,
  displayName: "",
  enableAnalyticsByMoveInDate: false,
  selfConfigEnabled: false,
  country: "CO",
  helpCenterUrl: undefined,
  isLoading: true,
  leadCategoryDefinitions: [],
  broadcastEnabled: false,
  collectionsEnabled: false,
  calComIntegrationEnabled: false,
  leadReassignmentEnabled: undefined,
  leadReassignmentAllowedEmails: undefined,
});

const MerchantContextProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const [merchantId, setMerchantId] = useState<string>("");
  const [merchantType, setMerchantType] = useState<"rentals" | "development" | null>(null);
  const [displayName, setDisplayName] = useState<string>("");
  const [enableAnalyticsByMoveInDate, setEnableAnalyticsByMoveInDate] = useState<boolean>(false);
  const [selfConfigEnabled, setSelfConfigEnabled] = useState<boolean>(false);
  const [country, setCountry] = useState<string>("CO");
  const [helpCenterUrl, setHelpCenterUrl] = useState<string | undefined>(undefined);
  const [broadcastEnabled, setBroadcastEnabled] = useState<boolean>(false);
  const [collectionsEnabled, setCollectionsEnabled] = useState<boolean>(false);
  const [calComIntegrationEnabled, setCalComIntegrationEnabled] = useState<boolean>(false);
  const [leadReassignmentEnabled, setLeadReassignmentEnabled] = useState<boolean | undefined>(
    undefined
  );
  const [leadReassignmentAllowedEmails, setLeadReassignmentAllowedEmails] = useState<
    string[] | undefined
  >(undefined);
  const [leadCategoryDefinitions, setLeadCategoryDefinitions] = useState<
    {
      label: string;
      value: {
        min?: number;
        max?: number;
      };
    }[]
  >([]);

  const { organization } = useStytchOrganization();

  // Fetch merchant data when merchantId is available
  const { data: merchantData } = trpc.merchants.get.useQuery(
    { id: merchantId },
    {
      enabled: !!merchantId,
      onSuccess: (data) => {
        setDisplayName(data.displayName ?? "");
        setEnableAnalyticsByMoveInDate(data.enableAnalyticsByMoveInDate ?? false);
        setSelfConfigEnabled(data.selfConfigEnabled ?? false);
        setCountry(data.country ?? "CO");
        setHelpCenterUrl(data.helpCenterUrl);
        setBroadcastEnabled(data.broadcastEnabled ?? false);
        setCollectionsEnabled(data.collectionsEnabled ?? false);
        setCalComIntegrationEnabled(data.calComIntegrationEnabled ?? false);
        setLeadReassignmentEnabled(data.leadReassignmentEnabled);
        setLeadReassignmentAllowedEmails(data.leadReassignmentAllowedEmails);
        setLeadCategoryDefinitions(data.leadCategoryDefinitions ?? []);
      },
    }
  );

  useEffect(() => {
    if (organization) {
      setMerchantId(organization.organization_slug);
    }
  }, [organization]);

  useEffect(() => {
    if (merchantData) {
      setMerchantType(merchantData.type);
    }
  }, [merchantData]);

  return (
    <MerchantContext.Provider
      value={{
        merchantId,
        merchantType,
        displayName,
        enableAnalyticsByMoveInDate,
        selfConfigEnabled,
        country,
        helpCenterUrl,
        broadcastEnabled,
        collectionsEnabled,
        calComIntegrationEnabled,
        leadReassignmentEnabled,
        leadReassignmentAllowedEmails,
        leadCategoryDefinitions,
        isLoading: !merchantData,
      }}
    >
      {children}
    </MerchantContext.Provider>
  );
};

export default MerchantContext;
export { MerchantContextProvider };
